import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

interface UserMenuProps {
  onOpenSettings: () => void;
  onUpgrade: () => void;
  isPro?: boolean;
  isAdmin?: boolean;
  onManageSubscription?: () => void;
  /** Opens the "Book a 15-minute setup call" form. */
  onBookSetupCall?: () => void;
  /** Replays the product tour (free or paid, by plan). */
  onReplayTour?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onOpenSettings, onUpgrade, isPro, isAdmin, onManageSubscription, onBookSetupCall, onReplayTour }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;

  const email = user.email || "";
  const initial = (email[0] || "?").toUpperCase();

  return (
    <div className="user-menu" ref={ref}>
      <button className="user-avatar" data-tour="user-menu" onClick={() => setOpen(!open)} aria-label="User menu">
        {initial}
      </button>
      {open && (
        <div className="user-dropdown">
          <div className="user-dropdown-email">{email}</div>
          <button
            className="user-dropdown-item"
            onClick={() => {
              setOpen(false);
              onOpenSettings();
            }}
          >
            Settings
          </button>
          {onBookSetupCall && (
            <button
              className="user-dropdown-item"
              onClick={() => {
                setOpen(false);
                onBookSetupCall();
              }}
            >
              Book a 15-min setup call
            </button>
          )}
          {onReplayTour && (
            <button
              className="user-dropdown-item"
              onClick={() => {
                setOpen(false);
                onReplayTour();
              }}
            >
              Take the tour
            </button>
          )}
          {isPro ? (
            onManageSubscription && (
              <button
                className="user-dropdown-item"
                onClick={() => {
                  setOpen(false);
                  onManageSubscription();
                }}
              >
                Manage Subscription
              </button>
            )
          ) : (
            <button
              className="user-dropdown-item user-dropdown-upgrade"
              onClick={() => {
                setOpen(false);
                onUpgrade();
              }}
            >
              Upgrade to Pro
            </button>
          )}
          {isAdmin && (
            <button
              className="user-dropdown-item user-dropdown-admin"
              onClick={() => {
                setOpen(false);
                navigate("/admin/feedback");
              }}
            >
              Admin →
            </button>
          )}
          <button
            className="user-dropdown-item user-dropdown-signout"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
